import React, { useState, useEffect } from 'react';
import { Shield, Plus, Globe, Mail, ToggleLeft, ToggleRight, Loader, AlertTriangle, X } from 'lucide-react';
import { userService } from '../../lib/userService';
import { useAuth } from '../../hooks/useAuth';
import { WhitelistEntry, UserRole } from '../../types';

interface AddWhitelistModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AddWhitelistModal: React.FC<AddWhitelistModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const { appUser } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    type: 'domain' as 'domain' | 'email',
    value: '',
    defaultRole: 'user' as UserRole
  });
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!appUser) return;

    if (!formData.value.trim()) {
      setError('Value is required');
      return;
    }

    if (formData.type === 'domain' && !formData.value.startsWith('@')) {
      setError('Domain must start with @');
      return;
    }

    if (formData.type === 'email' && !formData.value.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await userService.addWhitelistEntry(
        formData.type,
        formData.value.toLowerCase().trim(),
        formData.defaultRole,
        appUser.email
      );

      setFormData({
        type: 'domain',
        value: '',
        defaultRole: 'user'
      });
      
      onSuccess();
    } catch (error: any) {
      setError(error.message || 'Failed to add whitelist entry');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    setFormData({
      type: 'domain',
      value: '',
      defaultRole: 'user'
    });
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-md shadow-xl">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">Add Whitelist Entry</h2>
            <button
              onClick={handleClose}
              disabled={submitting}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>
          </div>

          {error && (
            <div className="bg-red-100 text-red-700 p-3 rounded mb-4">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Type</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, type: 'domain' }))}
                  className={`p-3 border rounded-lg text-center ${
                    formData.type === 'domain'
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <Globe size={20} className="mx-auto mb-1" />
                  <div className="text-sm font-medium">Domain</div>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, type: 'email' }))}
                  className={`p-3 border rounded-lg text-center ${
                    formData.type === 'email'
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <Mail size={20} className="mx-auto mb-1" />
                  <div className="text-sm font-medium">Email</div>
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                {formData.type === 'domain' ? 'Domain' : 'Email Address'}
              </label>
              <input
                type="text"
                value={formData.value}
                onChange={(e) => setFormData(prev => ({ ...prev, value: e.target.value }))}
                className="w-full p-3 border rounded-lg"
                placeholder={formData.type === 'domain' ? '@company.com' : 'user@company.com'}
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                {formData.type === 'domain' 
                  ? 'Allow all users from this domain to register'
                  : 'Allow this specific email address to register'
                }
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Default Role</label>
              <select
                value={formData.defaultRole}
                onChange={(e) => setFormData(prev => ({ ...prev, defaultRole: e.target.value as UserRole }))}
                className="w-full p-3 border rounded-lg"
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
                <option value="view-only">View Only</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Role automatically assigned to new registrations
              </p>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                disabled={submitting}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!formData.value.trim() || submitting}
                className="flex-1 bg-primary text-primary-foreground px-4 py-2 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 hover:bg-primary/90"
              >
                {submitting ? (
                  <>
                    <Loader className="animate-spin" size={16} />
                    Adding...
                  </>
                ) : (
                  'Add Entry'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export const AdminWhitelistManagement: React.FC = () => {
  const { appUser, permissions } = useAuth();
  const [whitelist, setWhitelist] = useState<WhitelistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [error, setError] = useState('');
  const [updatingEntry, setUpdatingEntry] = useState<string | null>(null);

  useEffect(() => {
    if (permissions.canManageWhitelist) {
      loadWhitelist();
    }
  }, [permissions.canManageWhitelist]);

  const loadWhitelist = async () => {
    try {
      setLoading(true);
      const entries = await userService.getWhitelistEntries();
      setWhitelist(entries);
    } catch (error) {
      console.error('Failed to load whitelist:', error);
      setError('Failed to load whitelist entries');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEntry = async (entryId: string) => {
    try {
      setUpdatingEntry(entryId);
      setError('');
      await userService.toggleWhitelistEntry(entryId);
      await loadWhitelist();
    } catch (error: any) {
      setError(error.message || 'Failed to update whitelist entry');
    } finally {
      setUpdatingEntry(null);
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const getRoleBadgeColor = (role: UserRole) => {
    switch (role) {
      case 'owner':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'admin':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'user':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'view-only':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (!permissions.canManageWhitelist) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="mx-auto h-16 w-16 text-yellow-500 mb-4" />
        <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
        <p className="text-gray-600">Only owners can manage the whitelist.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="animate-spin" size={32} />
        <span className="ml-2">Loading whitelist...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Shield size={28} className="text-primary" />
            Whitelist Management
          </h2>
          <p className="text-gray-600 mt-1">Control who can register for the system</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-primary text-primary-foreground px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-primary/90"
        >
          <Plus size={20} />
          Add Entry
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 text-red-400 mr-3" />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold">Authorized Domains & Emails</h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Value
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Default Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {whitelist.map((entry) => (
                <tr key={entry.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {entry.type === 'domain' ? (
                        <Globe size={16} className="text-blue-500 mr-2" />
                      ) : (
                        <Mail size={16} className="text-green-500 mr-2" />
                      )}
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                        entry.type === 'domain' 
                          ? 'bg-blue-100 text-blue-800 border-blue-200'
                          : 'bg-green-100 text-green-800 border-green-200'
                      }`}>
                        {entry.type}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                      {entry.value}
                    </code>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getRoleBadgeColor(entry.defaultRole)}`}>
                      {entry.defaultRole}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {entry.isActive ? (
                        <div className="flex items-center text-green-600">
                          <div className="w-2 h-2 bg-green-600 rounded-full mr-2"></div>
                          <span className="text-sm">Active</span>
                        </div>
                      ) : (
                        <div className="flex items-center text-red-600">
                          <div className="w-2 h-2 bg-red-600 rounded-full mr-2"></div>
                          <span className="text-sm">Inactive</span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div>
                      <div>{formatDate(entry.createdAt)}</div>
                      <div className="text-xs text-gray-400">by {entry.createdBy}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleToggleEntry(entry.id)}
                      disabled={updatingEntry === entry.id}
                      className={`inline-flex items-center px-3 py-1 rounded text-xs font-medium disabled:opacity-50 ${
                        entry.isActive
                          ? 'bg-red-100 text-red-700 hover:bg-red-200'
                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                      }`}
                    >
                      {updatingEntry === entry.id ? (
                        <Loader className="animate-spin" size={12} />
                      ) : (
                        <>
                          {entry.isActive ? (
                            <ToggleRight size={14} className="mr-1" />
                          ) : (
                            <ToggleLeft size={14} className="mr-1" />
                          )}
                          {entry.isActive ? 'Disable' : 'Enable'}
                        </>
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <AddWhitelistModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={() => {
          setShowAddModal(false);
          loadWhitelist();
        }}
      />
    </div>
  );
};